/**
 * Servicio para gestionar Orders
 */
import { apiService } from './api';
import { Order, CreateOrderDto, UpdateOrderDto, OrderListResponse, OrderStatus } from '../types/order.type';

export class OrdersService {
  private baseEndpoint = '/orders';

  /**
   * Obtiene todas las órdenes
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    status?: Order['status'];
  }): Promise<OrderListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.status) queryParams.append('status', params.status);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.baseEndpoint}?${queryString}` : this.baseEndpoint;
    
    const response = await apiService.get<any>(endpoint);
    
    // Función para mapear un objeto de orden del API al formato Order
    const mapOrderFromAPI = (apiOrder: any): Order => {
      return {
        id: apiOrder.id,
        userId: apiOrder.userId,
        productId: apiOrder.productId,
        status: apiOrder.status as OrderStatus,
        quantity: apiOrder.quantity ?? 1, // Si no viene quantity, usar 1 por defecto
        // El API devuelve el monto en centavos (59900 = $599.00 MXN); se convierte a pesos aquí
        total: ((apiOrder.amount ?? apiOrder.total ?? 0) / 100),
        currency: apiOrder.currency || 'USD',
        createdAt: typeof apiOrder.createdAt === 'string' ? new Date(apiOrder.createdAt) : apiOrder.createdAt,
        updatedAt: typeof apiOrder.updatedAt === 'string' ? new Date(apiOrder.updatedAt) : apiOrder.updatedAt,
        metadata: apiOrder.metadata,
      };
    };
    
    // Si la respuesta es un arreglo directo, convertirla al formato OrderListResponse
    if (Array.isArray(response)) {
      const mappedOrders = response.map(mapOrderFromAPI);
      return {
        orders: mappedOrders,
        total: mappedOrders.length,
        page: params?.page || 1,
        limit: params?.limit || mappedOrders.length,
      };
    }
    
    // Si viene como { orders: [...] } pero no tiene total, page, limit
    if (response && typeof response === 'object' && 'orders' in response && Array.isArray(response.orders)) {
      const mappedOrders = response.orders.map(mapOrderFromAPI);
      return {
        orders: mappedOrders,
        total: response.total ?? mappedOrders.length,
        page: response.page ?? params?.page ?? 1,
        limit: response.limit ?? params?.limit ?? mappedOrders.length,
      };
    }
    
    // Si ya viene en el formato OrderListResponse completo, mapear las órdenes
    if (response && typeof response === 'object' && 'orders' in response) {
      const mappedOrders = Array.isArray(response.orders) 
        ? response.orders.map(mapOrderFromAPI)
        : [];
      return {
        orders: mappedOrders,
        total: response.total ?? mappedOrders.length,
        page: response.page ?? params?.page ?? 1,
        limit: response.limit ?? params?.limit ?? mappedOrders.length,
      };
    }
    
    // Fallback: retornar estructura vacía
    return {
      orders: [],
      total: 0,
      page: params?.page || 1,
      limit: params?.limit || 0,
    };
  }

  /**
   * Obtiene una orden por ID
   */
  async getById(id: string): Promise<Order> {
    const response = await apiService.get<any>(`${this.baseEndpoint}/${id}`);
    
    // Mapear la respuesta del API al formato Order
    return {
      id: response.id,
      userId: response.userId,
      productId: response.productId,
      serviceId: response.serviceId,
      status: response.status as OrderStatus,
      quantity: response.quantity ?? 1,
      total: ((response.amount ?? response.total ?? 0) / 100),
      currency: response.currency || 'USD',
      createdAt: typeof response.createdAt === 'string' ? new Date(response.createdAt) : response.createdAt,
      updatedAt: typeof response.updatedAt === 'string' ? new Date(response.updatedAt) : response.updatedAt,
      metadata: response.metadata,
    };
  }

  /**
   * Crea una nueva orden
   */
  async create(data: CreateOrderDto): Promise<Order> {
    return apiService.post<Order>(this.baseEndpoint, data);
  }

  /**
   * Actualiza una orden
   */
  async update(id: string, data: UpdateOrderDto): Promise<Order> {
    return apiService.put<Order>(`${this.baseEndpoint}/${id}`, data);
  }

  /**
   * Actualiza parcialmente una orden
   */
  async patch(id: string, data: UpdateOrderDto): Promise<Order> {
    return apiService.patch<Order>(`${this.baseEndpoint}/${id}`, data);
  }

  /**
   * Elimina una orden
   */
  async delete(id: string): Promise<void> {
    return apiService.delete<void>(`${this.baseEndpoint}/${id}`);
  }

  /**
   * Cancela una orden
   */
  async cancel(id: string): Promise<Order> {
    return apiService.patch<Order>(`${this.baseEndpoint}/${id}`, {
      status: 'CANCELLED',
    });
  }

  /**
   * Simula el pago de una orden
   */
  async simulatePay(id: string): Promise<Order> {
    return apiService.post<Order>(`${this.baseEndpoint}/${id}/simulate-pay`, {});
  }

  }

// Instancia singleton del servicio
export const ordersService = new OrdersService();

